/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, TextField, Grid, Pagination as MuiPagination } from "@mui/material";
import { useSidebarFilters } from "../sidebar-filters";
import styles from "./pagination.module.css";
import { useInterestsTable } from "./table-context";
type Props = {
  interestListId: string;
  requestedPage?: number | string;
  navigate?: (to: string) => void;
}

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === "number" && Number.isFinite(value)
);

const GoToPageForm: React.FC<{
  goToPage: (pg: number) => void;
  nbPages: number
}> = ({ goToPage, nbPages }) => {
  const [jumpPage, setJumpPage] = useState("");
  const handleJumpPageChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault();
    setJumpPage(event.target.value);
  };

  const handleJump: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    goToPage(parseInt(jumpPage, 10));
  };

  return (<form onSubmit={handleJump}>
    <TextField
      label="Jump To Page"
      value={jumpPage}
      className={styles.jumpInput}
      onChange={handleJumpPageChange}
      margin="none"
      size="small"
      type="number"
      slotProps={{ htmlInput: { min: 1, max: nbPages } }}
      style={{ fontWeight: 300 }}
    />
    <Button
      className={styles.jumpButton}
      type="submit"
      variant="contained"
      color="primary">Go</Button>
  </form>);
};

const Pagination: React.FC<Props> = (props) => {
  const { deselectAllInterests } = useInterestsTable();
  const {
    pageState,
    setSelectedPage,
  } = useSidebarFilters();
  const navigate = useNavigate();

  const buildPath = (pg: number) => `/dash/manager/lists/${props.interestListId}/p/${pg}`;

  const doNavigateToPage = (pg: number) => {
    const to = buildPath(pg);
    // Guard against redirecting once we've left the interests list route (e.g. to
    // Duplicates). A late-firing pagination effect must not pull the user back, so
    // read the live pathname rather than a stale router snapshot. The guard also
    // covers the injected navigate — otherwise it is trivially bypassed.
    const currentPath = window.location.pathname;
    if (!currentPath.includes("/dash/manager/lists/")) return;
    if (currentPath === to) return;
    if (props.navigate) {
      props.navigate(to);
      return;
    }
    navigate(to);
  };

  const coerceRequestedPage = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const requestedPageNum = coerceRequestedPage(props.requestedPage);

  useEffect(() => {
    if (requestedPageNum === null) {
      setSelectedPage(0);
      doNavigateToPage(1);
    } else if (requestedPageNum <= 0 || (
      pageState.nbPages > 0 && requestedPageNum > pageState.nbPages)) {
      setSelectedPage(0);
      doNavigateToPage(1);
    }
  }, [requestedPageNum, pageState.nbPages]);

  useEffect(() => {
    if (requestedPageNum !== null) {
      const zeroBased = requestedPageNum - 1;
      if (pageState.page !== zeroBased) {
        setSelectedPage(zeroBased);
      }
    }
  }, [requestedPageNum, pageState.page]);

  const goToPage = (pg: number) => {
    deselectAllInterests();
    setSelectedPage(pg - 1);
    doNavigateToPage(pg);
  };


  const handleChange = (_event: React.ChangeEvent<unknown>, pg: number) => {
    goToPage(pg);
  };

  // Wrapping in 8px margin to fix the Grid container negative margin breaking scroll
  return (
    <div style={{ margin: "8px" }}>
      <Grid container className={styles.container} justifyContent="center" alignContent="center" spacing={2}>
        {pageState.page === 2000 && "The search is too broad. Please refine your search. We're limiting results to 50000."}
        {pageState.nbPages === 0 ? "No Results" : (
          <>
            <Grid>
              <div className={styles.paginationBorder}>
                <MuiPagination
                  count={pageState.nbPages}
                  page={(isFiniteNumber(pageState.page) ? pageState.page : 0) + 1}
                  onChange={handleChange}
                  showFirstButton
                  showLastButton
                  siblingCount={3}
                />
              </div>
            </Grid>
            <Grid>
              <GoToPageForm nbPages={pageState.nbPages} goToPage={goToPage} />
            </Grid></>
        )}
      </Grid>
    </div>
  );
};

export default Pagination;
